<template>
  <div class="queclink-tracker-statistics-global-chart">
    <div class="buttons">
      <button
        v-for="(entry, index) in entries"
        :key="entry.live.id"
        class="button is-small is-rounded is-size-7"
        :class="{
          'is-light has-text-weight-semibold': entry.live.id === selectedLiveId,
          'is-white': entry.live.id !== selectedLiveId,
          'queclink-tracker-statistics-global-chart__label--muted':
            selectedLiveId !== null && entry.live.id !== selectedLiveId,
        }"
        type="button"
        @click="selectLive(entry.live.id)"
      >
        <b-icon icon="circle" custom-size="fa-xs" :style="{ color: liveColor(index) }" />
        <span>{{ entry.live.name }}</span>
      </button>
    </div>

    <div class="queclink-tracker-statistics-global-chart__canvas is-relative">
      <b-loading :active="loading" :is-full-page="false" />
      <o-chart v-if="hasPoints" :options="chartOptions" />
      <p v-else-if="!loading" class="pt-2 is-size-7 has-text-grey has-text-centered">
        Aucune position sur ces lives.
      </p>
    </div>
  </div>
</template>

<script>
import OChart from '@components/Chart.vue'

const DECIMATION_SAMPLES = 1000

const SELECTED_BORDER_WIDTH = 2

const MUTED_ALPHA = '26'

const MILLISECONDS_PER_HOUR = 60 * 60 * 1000
const HOURS_PER_DAY = 24

const formatElapsed = (milliseconds) => {
  const hours = Math.round(milliseconds / MILLISECONDS_PER_HOUR)

  if (hours < HOURS_PER_DAY) {
    return `${hours} h`
  }

  const days = Math.floor(hours / HOURS_PER_DAY)
  const remainingHours = hours % HOURS_PER_DAY

  return remainingHours ? `${days} j ${remainingHours} h` : `${days} j`
}

const CURVE_COLORS = [
  '#2D62DD',
  '#2DAF32',
  '#A02DDD',
  '#FF741F',
  '#229CA0',
  '#E63737',
  '#CE3399',
  '#7B4336',
  '#585858',
]

const CHART_OPTIONS = {
  animation: false,
  parsing: false,
  normalized: true,
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'linear',
      min: 0,
      bounds: 'data',
      ticks: {
        callback: (value) => formatElapsed(value),
      },
    },
    y: {
      type: 'linear',
      suggestedMin: 0,
      suggestedMax: 100,
    },
  },
  interaction: {
    mode: 'nearest',
    intersect: false,
    axis: 'xy',
  },
  plugins: {
    decimation: {
      enabled: true,
      algorithm: 'lttb',
      samples: DECIMATION_SAMPLES,
    },
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: true,
      callbacks: {
        title: (items) => formatElapsed(items[0].parsed.x),
        label: function (context) {
          return ` ${context.dataset.label} — ${context.parsed.y} %`
        },
      },
    },
  },
}

export default {
  name: 'QueclinkTrackerStatisticsGlobalChart',

  components: { OChart },

  props: {
    entries: {
      type: Array,
      required: true,
      validator: (v) =>
        v.every((entry) => ['live', 'series', 'seriesLoading'].every((key) => key in entry)),
    },
  },

  data() {
    return {
      selectedLiveId: null,
    }
  },

  computed: {
    loading() {
      return this.entries.some((entry) => entry.seriesLoading)
    },

    hasPoints() {
      return this.entries.some((entry) => entry.series.length)
    },

    chartOptions() {
      const datasets = this.entries.flatMap((entry, index) => {
        if (!entry.series.length) {
          return []
        }

        const color = this.liveColor(index)
        const isSelected = entry.live.id === this.selectedLiveId
        const isMuted = this.selectedLiveId !== null && !isSelected
        // elapsed time since the live's first point, so the lives overlay and compare
        const liveStartedAt = Math.min(
          ...entry.series.map((series) => series.points[0]?.x ?? Infinity),
        )

        return entry.series.map((series) => ({
          liveId: entry.live.id,
          label: entry.live.name,
          data: series.points.map((point) => ({ x: point.x - liveStartedAt, y: point.y })),
          borderColor: isMuted ? `${color}${MUTED_ALPHA}` : color,
          backgroundColor: isMuted ? `${color}${MUTED_ALPHA}` : color,
          borderWidth: isSelected ? SELECTED_BORDER_WIDTH : 1.5,
          order: isSelected ? 0 : 1,
          borderJoinStyle: 'round',
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBorderWidth: 2,
          pointHoverBorderColor: '#fff',
        }))
      })

      return {
        type: 'line',
        options: { ...CHART_OPTIONS, onClick: this.selectClickedCurve },
        data: { datasets },
      }
    },
  },

  methods: {
    liveColor(index) {
      return CURVE_COLORS[index % CURVE_COLORS.length]
    },

    selectLive(liveId) {
      this.selectedLiveId = liveId === this.selectedLiveId ? null : liveId
    },

    selectClickedCurve(event, activeElements, chart) {
      const dataset = activeElements.length
        ? chart.data.datasets[activeElements[0].datasetIndex]
        : null

      this.selectLive(dataset ? dataset.liveId : this.selectedLiveId)
    },
  },
}
</script>

<style lang="scss" scoped>
.queclink-tracker-statistics-global-chart {
  &__label--muted {
    opacity: 0.45;
  }

  &__canvas {
    height: 320px;
  }
}
</style>
